import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Agent, GetAgentsResponse } from '@dreamai/types';
import api from '../api';

export interface AgentOption {
  value: string;
  label: string;
  dreamieImageUrl: string | undefined;
  predominantColour: string | undefined;
}

export type AgentMode = 'personal' | 'business' | 'system';

interface AgentContextType {
  selectedAgent: AgentOption | null;
  setSelectedAgent: Dispatch<SetStateAction<AgentOption | null>>;
  agents: Agent[];
  setAgents: Dispatch<SetStateAction<Agent[]>>;
  getAgentById: (agentId: string) => Agent | undefined;
  loading: boolean;
  error: string | null;
  loadAgents: () => Promise<void>;
  agentMode: AgentMode;
  setAgentMode: Dispatch<SetStateAction<AgentMode>>;
  activeAgent: Agent | undefined;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

interface AgentProviderProps {
  children: ReactNode;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentMode, setAgentMode] = useState<AgentMode>('personal');

  // Derive which agent types are available
  const hasPA = useMemo(
    () => agents.some((a) => (a.agent_type as string) === 'Personal Assistant'),
    [agents],
  );
  const hasSA = useMemo(
    () => agents.some((a) => (a.agent_type as string) === 'Sales Assistant'),
    [agents],
  );
  const hasSystemAgent = useMemo(
    () => agents.some((a) => (a.agent_type as string) === 'System Assistant'),
    [agents],
  );

  // Correct agentMode if the current mode's agent type doesn't exist
  useEffect(() => {
    if (agents.length === 0) return;
    if (agentMode === 'personal' && !hasPA) {
      setAgentMode(hasSA ? 'business' : 'system');
    } else if (agentMode === 'business' && !hasSA) {
      setAgentMode(hasPA ? 'personal' : 'system');
    } else if (agentMode === 'system' && !hasSystemAgent) {
      setAgentMode(hasPA ? 'personal' : 'business');
    }
  }, [agents, agentMode, hasPA, hasSA, hasSystemAgent]);

  // Derive the active agent based on agentMode
  const activeAgent = useMemo(() => {
    const targetType =
      agentMode === 'personal'
        ? 'AI Secretary'
        : agentMode === 'business'
          ? 'AI Secretary'
          : 'System Assistant';
    return (
      agents.find((a) => (a.agent_type as string) === targetType) || agents[0]
    );
  }, [agents, agentMode]);

  const getAgentById = (agentId: string): Agent | undefined => {
    return agents.find((agent) => agent.id === agentId);
  };

  const loadAgents = useCallback(async () => {
    // If already loaded and not in error state, don't reload
    if (agents.length > 0 && !error) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetAgentsResponse>('/agents', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setAgents(response.data.agents);
      } else {
        setError('Failed to load agents');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, agents.length, error]);

  return (
    <AgentContext.Provider
      value={{
        selectedAgent,
        setSelectedAgent,
        agents,
        setAgents,
        getAgentById,
        loading,
        error,
        loadAgents,
        agentMode,
        setAgentMode,
        activeAgent,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = (): AgentContextType => {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};
